import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { neonAuth, type NeonUser, type NeonSession } from '@/integrations/neon-auth';

// Compatibility layer mapping Neon Auth user to internal interface
interface CompatUser {
  id: string;
  email?: string;
  user_metadata?: {
    full_name?: string;
    avatar_url?: string;
  };
}

interface CompatSession {
  user: CompatUser;
  access_token?: string;
}

interface AuthContextType {
  // Compatibility interface
  session: CompatSession | null;
  user: CompatUser | null;
  loading: boolean;
  signOut: () => Promise<void>;

  // Neon Auth specific
  neonUser: NeonUser | null;
  neonSession: NeonSession | null;
  isAuthenticated: boolean;

  // Auth methods
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signUp: (email: string, password: string, name?: string) => Promise<{ error?: string }>;
  signInWithOAuth: (provider: 'google' | 'github') => Promise<void>;
  forgotPassword: (email: string) => Promise<{ error?: string }>;
  resetPassword: (newPassword: string, token?: string) => Promise<{ error?: string }>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Convert Neon Auth user to compatibility format
 */
function toCompatUser(neonUser: NeonUser | null): CompatUser | null {
  if (!neonUser) return null;

  return {
    id: neonUser.id,
    email: neonUser.email || undefined,
    user_metadata: {
      full_name: neonUser.name || undefined,
      avatar_url: neonUser.image || undefined,
    },
  };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [neonUser, setNeonUser] = useState<NeonUser | null>(null);
  const [neonSession, setNeonSession] = useState<NeonSession | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch current session
  const refreshSession = useCallback(async () => {
    try {
      const sessionData = await neonAuth.getSession();
      if (sessionData?.data) {
        setNeonUser(sessionData.data.user as NeonUser);
        setNeonSession(sessionData.data.session as NeonSession);
      } else {
        setNeonUser(null);
        setNeonSession(null);
      }
    } catch (error) {
      console.error('Error getting session:', error);
      setNeonUser(null);
      setNeonSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Get initial session
    refreshSession();

    // Listen for auth state changes via the auth client
    // Better Auth uses session events
    const handleSessionChange = () => {
      refreshSession();
    };

    // Set up event listener for storage changes (cross-tab sync)
    window.addEventListener('storage', e => {
      if (e.key?.includes('better-auth') || e.key?.includes('neon-auth')) {
        handleSessionChange();
      }
    });

    // Poll for session changes (fallback)
    const interval = setInterval(refreshSession, 60000); // Check every minute

    return () => {
      window.removeEventListener('storage', handleSessionChange);
      clearInterval(interval);
    };
  }, [refreshSession]);

  const signOut = async () => {
    try {
      await neonAuth.signOut();
      setNeonUser(null);
      setNeonSession(null);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const signIn = async (email: string, password: string): Promise<{ error?: string }> => {
    try {
      const result = await neonAuth.signIn({
        email,
        password,
        callbackURL: '/',
      });

      if (result?.error) {
        return { error: result.error.message || 'Sign in failed' };
      }

      await refreshSession();
      return {};
    } catch (error) {
      console.error('Sign in error:', error);
      return { error: error instanceof Error ? error.message : 'Sign in failed' };
    }
  };

  const signUp = async (
    email: string,
    password: string,
    name?: string
  ): Promise<{ error?: string }> => {
    try {
      const result = await neonAuth.signUp({
        email,
        password,
        name,
        callbackURL: '/',
      });

      if (result?.error) {
        return { error: result.error.message || 'Sign up failed' };
      }

      await refreshSession();
      return {};
    } catch (error) {
      console.error('Sign up error:', error);
      return { error: error instanceof Error ? error.message : 'Sign up failed' };
    }
  };

  const signInWithOAuth = async (provider: 'google' | 'github'): Promise<void> => {
    try {
      await neonAuth.signInWithOAuth({
        provider,
        callbackURL: `${window.location.origin}/auth/callback`,
        errorCallbackURL: `${window.location.origin}/auth?error=oauth_error`,
      });
    } catch (error) {
      console.error('OAuth sign in error:', error);
      throw error;
    }
  };

  const forgotPassword = async (email: string): Promise<{ error?: string }> => {
    try {
      const result = await neonAuth.forgotPassword(
        email,
        `${window.location.origin}/auth/reset-password`
      );

      if (result?.error) {
        return { error: result.error.message || 'Password reset request failed' };
      }

      return {};
    } catch (error) {
      console.error('Forgot password error:', error);
      return { error: error instanceof Error ? error.message : 'Password reset request failed' };
    }
  };

  const resetPassword = async (
    newPassword: string,
    token?: string
  ): Promise<{ error?: string }> => {
    try {
      const result = await neonAuth.resetPassword({
        newPassword,
        token,
      });

      if (result?.error) {
        return { error: result.error.message || 'Password reset failed' };
      }

      return {};
    } catch (error) {
      console.error('Reset password error:', error);
      return { error: error instanceof Error ? error.message : 'Password reset failed' };
    }
  };

  // Create compatibility session
  const compatUser = toCompatUser(neonUser);
  const session: CompatSession | null = compatUser
    ? { user: compatUser, access_token: neonSession?.token }
    : null;
  const isAuthenticated = !!neonUser;

  return (
    <AuthContext.Provider
      value={{
        // Compatibility
        session,
        user: compatUser,
        loading,
        signOut,

        // Neon Auth specific
        neonUser,
        neonSession,
        isAuthenticated,

        // Auth methods
        signIn,
        signUp,
        signInWithOAuth,
        forgotPassword,
        resetPassword,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Export types for use elsewhere
export type { CompatUser, CompatSession, AuthContextType };
